import { useState } from 'react'
import { api } from '../../../lib/api.js'
import { toast } from 'sonner'

const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif']

export function useImageUpload({ onSuccess } = {}) {
  const [uploading, setUploading] = useState(false)
  const [uploadForm, setUploadForm] = useState({ image_type_id: '', is_active: false })
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploadProgress, setUploadProgress] = useState(null)
  const [dragOver, setDragOver] = useState(false)

  const addFiles = (rawFiles) => {
    const accepted = Array.from(rawFiles).filter((f) => ACCEPTED_MIME.includes(f.type))
    if (accepted.length < rawFiles.length) {
      toast.error('Beberapa file dilewati — hanya jpg, png, webp, gif, avif yang diterima.')
    }
    const entries = accepted.map((f) => ({
      file: f,
      // Revoke on unmount via cleanup in component
      preview: URL.createObjectURL(f),
      title: '',
      status: 'pending', // pending | uploading | done | error
      error: null,
    }))
    setUploadFiles((prev) => [...prev, ...entries])
  }

  const removeFile = (index) => {
    setUploadFiles((prev) => {
      // Revoke the object URL to free memory
      URL.revokeObjectURL(prev[index].preview)
      return prev.filter((_, i) => i !== index)
    })
  }

  const clearAll = () => {
    setUploadFiles((prev) => { prev.forEach((f) => URL.revokeObjectURL(f.preview)); return [] })
    setUploadProgress(null)
  }

  const updateFile = (index, patch) =>
    setUploadFiles((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)))

  const open = () => setUploading(true)
  const close = () => { setUploading(false); clearAll() }

  const onUpload = async (e) => {
    e.preventDefault()
    if (uploadFiles.length === 0) return toast.error('Pilih minimal satu gambar')
    if (!uploadForm.image_type_id) return toast.error('Pilih tipe gambar terlebih dahulu')

    const pending = uploadFiles.filter((f) => f.status === 'pending')
    setUploadProgress({ done: 0, total: pending.length })

    let successCount = 0
    for (let i = 0; i < uploadFiles.length; i++) {
      const entry = uploadFiles[i]
      if (entry.status !== 'pending') continue

      updateFile(i, { status: 'uploading' })

      const fd = new FormData()
      fd.append('image_type_id', uploadForm.image_type_id)
      fd.append('file', entry.file)
      if (entry.title.trim()) fd.append('title', entry.title.trim())
      fd.append('is_active', uploadForm.is_active ? '1' : '0')

      try {
        await api.post('/admin/images', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        updateFile(i, { status: 'done' })
        successCount++
      } catch (err) {
        // Only expose safe error message from backend, never raw stack
        const safeMsg = err.response?.data?.message || 'Gagal mengunggah'
        updateFile(i, { status: 'error', error: safeMsg })
      }

      setUploadProgress((p) => ({ ...p, done: (p?.done ?? 0) + 1 }))
    }

    if (successCount > 0) {
      toast.success(`${successCount} gambar berhasil diunggah`)
      onSuccess?.(uploadForm.image_type_id)
    }
    setUploadProgress(null)
  }

  return {
    uploading, open, close,
    uploadForm, setUploadForm,
    uploadFiles, addFiles, removeFile, clearAll, updateFile,
    uploadProgress,
    dragOver, setDragOver,
    onUpload,
  }
}
